import React from 'react';
import { TextV2 } from '@withjoy/joykit';
import { FormattedCurrency } from '@shared/utils/currency';
import type { CashFundConfirmationProps } from './CashFundConfirmation';
import {
  StyledCashFundCollectionMembersTitle,
  StyledCashFundCollectionMembersWrapper,
  StyledCollectionItemTitle,
  StyledCollectionMemberContainer,
  StyledCollectionMemberInfo,
  StyledCollectionMemberPhoto,
  styles,
} from './CashFundCollectionMembers.styles';
import { useRouterHelper, useTranslation } from '@shared/core';
import type { MinimalProductWithDetails } from '../../../Catalog/utils/createTelemetryPayload';
import { withWindow } from '@shared/utils/withWindow';
import { routePaths } from '@apps/registry/admin/Registry.routes';
import { qs } from '@shared/core/queryString';
import { useCatalogLoggingContext } from '../../../Catalog/Catalog.logging';

type CashFundCollectionMembersProps = Pick<CashFundConfirmationProps, 'collectionMembers' | 'selectedCategory' | 'cashFundTitle'>;

const CashFundCollectionMembers = ({ collectionMembers, selectedCategory, cashFundTitle }: CashFundCollectionMembersProps) => {
  const { t2 } = useTranslation('catalogRegistry');
  const { collectionMembersTitle } = t2('cashFundCatalog');

  const routeHelpers = useRouterHelper();
  const { trackProductTileClickAction } = useCatalogLoggingContext();

  const onProductClick = (product: MinimalProductWithDetails, position: number) => {
    trackProductTileClickAction({
      product,
      position,
      page: 'recommendedProducts',
      component: 'cashFundRelatedProducts',
      method: 'productTile',
      activeRelatedProduct: cashFundTitle,
    });

    const path = routeHelpers.buildPath(
      qs.stringifyUrl({
        url: `${routePaths.shopItem.path}/${product.id}`,
        query: {
          relatedProductsQuickAdd: cashFundTitle,
        },
      }),
    );
    withWindow(window => window.open(path, '_blank'));
  };

  return (
    <StyledCashFundCollectionMembersWrapper __css={styles.cashFundCollectionMembersWrapper}>
      <StyledCashFundCollectionMembersTitle
        typographyVariant="hed2"
        __css={styles.cashFundCollectionMembersTitle}
      >
        {collectionMembersTitle}
      </StyledCashFundCollectionMembersTitle>
      {collectionMembers!.map((item, index) => {
        return (
          <StyledCollectionMemberContainer
            onClick={() => onProductClick(item.catalogItemData!, index)}
            key={item.id}
            __css={styles.collectionMemberContainer}
          >
            <StyledCollectionMemberPhoto
              __css={styles.collectionMemberPhoto}
              backgroundImage={item.catalogItemData?.photos?.[0]?.url ? `url("${item.catalogItemData?.photos?.[0]?.url}?rendition=small&product=true")` : ''}
            />
            <StyledCollectionMemberInfo __css={styles.collectionMemberInfo}>
              <TextV2
                typographyVariant="label2"
                color="mono10"
              >
                {item.catalogItemData?.brand?.name}
              </TextV2>
              <StyledCollectionItemTitle
                typographyVariant="hed1"
                __css={styles.collectionItemTitle}
              >
                {item.catalogItemData?.title}
              </StyledCollectionItemTitle>
              <TextV2
                typographyVariant="label2"
                color="mono10"
              >
                <FormattedCurrency
                  priceFloatingPointDecimalString={item.catalogItemData?.price?.floatingPointDecimalString || '0'}
                  priceCurrencyCode={item.catalogItemData?.price?.currency?.code}
                  formatForm="short"
                />
              </TextV2>
            </StyledCollectionMemberInfo>
          </StyledCollectionMemberContainer>
        );
      })}
    </StyledCashFundCollectionMembersWrapper>
  );
};

export default CashFundCollectionMembers;
